import dayjs from 'dayjs';

/**
 * When an unpaid invoice tips a facility into suspension.
 *
 * The rule lives on the backend: a job suspends the facility once the invoice
 * passes its due date plus a grace period, and that period is configured per
 * environment — deliberately short outside production, so a full cycle can be
 * tested without waiting weeks.
 *
 * The apps therefore must not compute or quote that window. They previously
 * did, in six places, each promising "72 hours" derived from `dueDate + 3
 * days` — a figure the backend never honoured, so a facility could be
 * suspended while the screen still told it three days remained.
 *
 * The API now sends `suspensionDeadline` on both the staffing and subscription
 * invoice, computed from the same constant the suspension job enforces. Pass
 * it through; never derive it.
 */

/** True once the invoice is past its due date. */
export function isInvoicePastDue(dueDate: string | Date | null): boolean {
  return dueDate ? dayjs().isAfter(dayjs(dueDate)) : false;
}

/**
 * What to tell the payer about an overdue invoice.
 *
 * Names the suspension date where the API has sent one, and otherwise states
 * the consequence without predicting when it lands — an older invoice, or one
 * already paid or cancelled, carries no deadline.
 *
 * @param daysOverdue from the API; a fact rather than a forecast.
 * @param facilityName names the facility, for admins looking at someone
 * else's invoice rather than their own.
 * @param suspensionDeadline from the API. Never compute this.
 */
export function overdueInvoiceMessage(
  daysOverdue?: number | null,
  facilityName?: string,
  suspensionDeadline?: string | null
): string {
  const age =
    daysOverdue && daysOverdue > 0
      ? `${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'} overdue.`
      : 'Past due.';

  // Naming the facility already identifies who is at risk, so the consequence
  // clause stays bare; the first-person variant needs "your account".
  const payer = facilityName
    ? `${facilityName} should pay this invoice`
    : 'Please pay this invoice';
  const consequence = facilityName
    ? 'to avoid being suspended'
    : 'to avoid your account being suspended';

  if (suspensionDeadline) {
    const by = dayjs(suspensionDeadline).format('MMM D, YYYY');
    return `${age} ${payer} by ${by} ${consequence}.`;
  }

  return `${age} ${payer} ${consequence}.`;
}

/** Shown once the account is actually suspended over an unpaid invoice. */
export const SUSPENDED_FOR_INVOICE_MESSAGE =
  'Your account has been suspended due to this unpaid invoice. Please contact ' +
  'support to resolve this issue or pay the invoice immediately.';
